import { Children, forwardRef, useRef } from 'react'
import { Box, type BoxProps } from '../Box'
import { TextWithCapsize as Text } from '../Text'
import {
  buttonBase,
  primaryButtonCss,
  outlineButtonCss,
  ghostButtonCss,
  flatButtonCss,
  invertedButtonCss,
  smallButtonCss,
  regularButtonCss,
  largeButtonCss,
  squareButtonCss,
  greenButtonCss,
} from './Button.v3.css'

type Variant = 'primary' | 'inverted' | 'outline' | 'ghost' | 'flat' | 'green'

interface Props {
  loading?: boolean
  variant?: Variant
  size?: 'large' | 'small' | 'regular' | 'square'
}

export type ButtonComponentProps = Props
export type ButtonProps = Omit<
  BoxProps,
  'formAction' | keyof ButtonComponentProps
> &
  ButtonComponentProps

export const ButtonV3 = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      className,
      variant = 'primary',
      children,
      loading,
      disabled,
      size = 'regular',
      width = [null, 'fit-content'],
      display = 'flex',
      ...rest
    } = props
    const fallbackRef = useRef<HTMLButtonElement>(null)
    const normalizedRef = ref || fallbackRef
    const isPressed = useRef(false)

    return (
      <Box
        as="button"
        type="button"
        ref={normalizedRef}
        disabled={disabled ?? loading}
        aria-busy={loading}
        className={[
          buttonBase,
          {
            [largeButtonCss]: size === 'large',
            [regularButtonCss]: size === 'regular',
            [smallButtonCss]: size === 'small',
            [squareButtonCss]: size === 'square',
            [primaryButtonCss]: variant === 'primary',
            [invertedButtonCss]: variant === 'inverted',
            [outlineButtonCss]: variant === 'outline',
            [ghostButtonCss]: variant === 'ghost',
            [flatButtonCss]: variant === 'flat',
            [greenButtonCss]: variant === 'green',
          },
          className,
        ]}
        width={width}
        display={display}
        {...rest}
        onMouseDown={() => {
          isPressed.current = true

          if ('current' in normalizedRef && normalizedRef.current) {
            normalizedRef.current.style.transform = 'scale(0.98)'
          }
        }}
        onMouseUp={() => {
          if (
            isPressed.current &&
            'current' in normalizedRef &&
            normalizedRef.current
          ) {
            normalizedRef.current.style.transform = ''
          }

          isPressed.current = false
        }}
        onMouseOut={() => {
          if (
            isPressed.current &&
            'current' in normalizedRef &&
            normalizedRef.current
          ) {
            normalizedRef.current.style.transform = ''
          }

          isPressed.current = false
        }}
      >
        {Children.map(children, (child, index) => {
          if (typeof child === 'string') {
            return (
              <Text
                variant={null}
                size={size === 'large' ? '14' : 'xs'}
                as="span"
                key={index}
              >
                {child}
              </Text>
            )
          }

          return child
        })}
      </Box>
    )
  }
)
